import { useState, memo, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Button, InputLabel, TextField, Typography } from '@mui/material';
import { BaseSelect } from '~/components/common';
import { DeleteCircle } from '~/assets/icons';
import { NumericFormat } from 'react-number-format';
import { cast, handleApiError } from '~/utils/helper';
import { IInvoice, IInvoiceItem } from '~/utils/interface/invoice';
import AddEstimateItemDialog from './add-estimate-item-dialog/AddEstimateItemDialog';
import { IEstimateList, IResponseEstimateItem } from '~/utils/interface/estimate';
import { handleGetEstimate, handleGetEstimateItems, handleGetEstimateList } from '~/thunks/estimate/estimateThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { DIALOG_MODE_EDIT, FIXED_VALUE } from '~/utils/constants/common';
import dayjs from 'dayjs';
import { SelectOption } from '~/utils/types/common';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';

type InvoiceListItemProps = {
  form: any;
  editData?: IInvoice;
  jobId?: string;
  companyTax: number;
  mode?: string;
};

function InvoiceListItem(props: InvoiceListItemProps) {
  //#region Destructuring Props
  const { form } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/invoice');
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { filterList } = useAppSelector((state) => state.estimate);
  const { units } = useAppSelector((state) => state.master);
  const { areaScopes } = useAppSelector((state) => state.areaScope);

  //#endregion Selector

  //#region Declare State
  const [openAddEstimateItemDialog, setOpenAddEstimateItemDialog] = useState<boolean>(false);
  const [estimateItems, setEstimateItems] = useState<Array<IResponseEstimateItem>>([]);
  const [estimateList, setEstimateList] = useState<Array<SelectOption>>([]);
  const [estimateSelected, setEstimateSelected] = useState<any>('');
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.jobId) {
      (async () => {
        try {
          const response = await dispatch(
            handleGetEstimateList({ jobId: props.jobId ?? '', data: { ...filterList, limit: 0 } })
          ).unwrap();
          const estimateListData = response.responses.map((item: IEstimateList, index) => ({
            id: item.id,
            name:
              `Estimate ${index + 1} ($${item.totalAmount} - Created at ${dayjs(item.createdAt).format(
                'MM/DD/YYYY'
              )})` || '',
          }));
          if (estimateListData.length === 1 && props.mode !== DIALOG_MODE_EDIT)
            setEstimateSelected(estimateListData[0].id);
          setEstimateList(estimateListData);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [props.jobId, filterList, dispatch]);
  useEffect(() => {
    if (props?.jobId) {
      (async () => {
        try {
          const response = await dispatch(handleGetEstimateItems({ jobId: String(props.jobId) })).unwrap();

          setEstimateItems(
            response.responses.map((item) => ({
              ...item,
              name: item.priceItemName,
            }))
          );
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.jobId]);

  useEffect(() => {
    if (form.values?.invoiceItems?.length) {
      calculateSubtotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.invoiceItems]);

  useEffect(() => {
    if (props.editData?.invoiceItems?.length) {
      const items = props.editData.invoiceItems.map((data: IInvoiceItem) => {
        let mapItem: IInvoiceItem = {
          id: data.id,
          estimateItemId: data.estimateItemId,
          priceItemCode: data.priceItemCode,
          priceItemName: data.priceItemName,
          description: data.description,
          location: data.location,
          unitPrice: Number(data.unitPrice ?? 0),
          quantity: Number(data.quantity ?? 1),
          taxPercent: Number(data.taxPercent ?? 0),
          unit: data.unit,
        };
        mapItem.amount = calculateItemAmount(mapItem);

        return mapItem;
      });

      form.setFieldValue('invoiceItems', items);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editData]);

  useEffect(() => {
    if (form.values?.invoiceItems?.length) {
      const invoiceItems = form.values.invoiceItems.map((invoiceItem: IInvoiceItem) => {
        let mapItem: IInvoiceItem = {
          ...invoiceItem,
          taxPercent: props.companyTax || 0,
        };

        mapItem.amount = calculateItemAmount(mapItem);
        return mapItem;
      });

      form.setFieldValue(`invoiceItems`, invoiceItems);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.companyTax]);
  //#endregion Implement Hook

  //#region Handle Function
  useEffect(() => {
    if (estimateList && estimateSelected) {
      (async () => {
        try {
          const response = await dispatch(handleGetEstimate(estimateSelected)).unwrap();
          const items = response.estimateItems.map((data: any) => {
            let mapItem: IInvoiceItem = {
              id: data.id,
              estimateItemId: data.id,
              priceItemCode: data.priceItemCode,
              priceItemName: data.priceItemName,
              description: data.description,
              location: data.location,
              unitPrice: Number(data.unitPrice ?? 0),
              quantity: Number(data.quantity ?? 1),
              taxPercent: Number(data.taxPercent ?? 0),
              unit: data.unit,
            };
            mapItem.amount = calculateItemAmount(mapItem);

            return mapItem;
          });
          form.setFieldValue('invoiceItems', [...(form.values.invoiceItems || []), ...items]);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [estimateSelected, estimateList]);

  useEffect(() => {
    if (props.jobId) {
      dispatch(handleGetAreaScopeList({ jobId: props.jobId }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, props.jobId]);

  const handleOnChange = (event: React.ChangeEvent<HTMLButtonElement>, index: number) => {
    const estimateItem = estimateItems?.find((item) => Number(item.id) === Number(event.target.value)) as IInvoiceItem;
    if (estimateItem) {
      form.setFieldValue(`invoiceItems.${index}`, {
        estimateItemId: cast(estimateItem.id),
        priceItemName: cast(estimateItem.priceItemName),
        priceItemCode: cast(estimateItem.priceItemCode),
        description: cast(estimateItem.description),
        unitPrice: Number(estimateItem.unitPrice),
        location: cast(estimateItem.location),
        quantity: Number(estimateItem.quantity ?? 1),
        amount: calculateItemAmount(estimateItem),
        unit: estimateItem.unit,
      });
    }
  };

  const handleAddPriceItem = () => {
    setOpenAddEstimateItemDialog(true);
  };

  const handleDelete = (key: number) => {
    form.setFieldValue(
      'invoiceItems',
      form.values.invoiceItems.filter((item: IInvoiceItem, index: number) => index !== key)
    );
  };

  const calculateItemAmount = (item: IInvoiceItem) => {
    const subtotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 1);
    return subtotal + (subtotal * (item?.taxPercent || 0)) / 100;
  };

  const handleOpenAddEstimateItemDialog = (value: boolean) => {
    setOpenAddEstimateItemDialog(value);
  };

  const handleSelectedEstimateItems = (estimateItems: IResponseEstimateItem[]) => {
    const invoiceItems = estimateItems as IInvoiceItem[];
    form.setFieldValue('invoiceItems', [
      ...(form.values.invoiceItems || []),
      ...invoiceItems.map((item) => {
        let mapItem: IInvoiceItem = {
          estimateItemId: cast(item.id),
          priceItemName: cast(item.priceItemName),
          priceItemCode: cast(item.priceItemCode),
          unitPrice: Number(item.unitPrice),
          description: cast(item.description),
          location: cast(item.location),
          quantity: Number(item.quantity ?? 1),
          taxPercent: item.taxPercent,
          unit: item.unit,
        };

        mapItem.amount = calculateItemAmount(mapItem);
        return mapItem;
      }),
    ]);
  };

  const calculateSubtotal = () => {
    let subtotal = 0;
    let total = 0;
    form.values.invoiceItems.forEach((item: IInvoiceItem) => {
      subtotal += (Number(item.unitPrice) ?? 0) * (Number(item.quantity) ?? 1);
      total += Number(item.amount) || 0;
    });

    form.setFieldValue('subtotal', parseFloat(subtotal.toFixed(FIXED_VALUE)));
    form.setFieldValue('total', parseFloat(total.toFixed(FIXED_VALUE)));
  };
  //#endregion Handle Function

  return (
    <Box>
      <Box className='bg-[#F3F9FF] w-full my-4 p-5 font-[600] text-[14px] border-y-2 border-solid border-y-[#e5eaef] flex justify-between items-center'>
        {t('estimateItems.title')}
      </Box>
      <Box className='p-5'>
        <Box className='flex justify-end mb-2'>
          <Typography className='px-2 content-center'>{t('importFromEstimate')}</Typography>
          <Box className='w-[400px]'>
            <BaseSelect
              name='estimate'
              value={estimateSelected}
              options={estimateList}
              onChange={(event) => {
                setEstimateSelected(event.target.value);
              }}
            ></BaseSelect>
          </Box>
        </Box>
        <Box className='overflow-auto max-h-[300px]'>
          {form.values.invoiceItems?.map((item: IInvoiceItem, index: number) => (
            <Box className='flex justify-between items-center gap-[20px] my-3 w-full' key={index}>
              <Box className='gap-[20px] w-full flex'>
                <Box className='flex-[0_0_10%]'>
                  <InputLabel> {t('estimateItems.name')}</InputLabel>
                  <BaseSelect
                    name={`invoiceItems.${index}.estimateItemId`}
                    className='w-full'
                    value={cast(form.values.invoiceItems[index]?.estimateItemId)}
                    options={estimateItems}
                    onChange={(event) => handleOnChange(event, index)}
                  ></BaseSelect>
                </Box>

                <Box className='flex-[0_0_7%]'>
                  <InputLabel>{t('estimateItems.code')}</InputLabel>
                  <TextField
                    name={`invoiceItems.${index}.priceItemCode`}
                    disabled
                    value={cast(form.values.invoiceItems[index]?.priceItemCode)}
                    onChange={form.handleChange}
                  />
                </Box>

                <Box className='flex-[0_0_10%]'>
                  <InputLabel>{t('estimateItems.quantity')}</InputLabel>
                  <NumericFormat
                    customInput={TextField}
                    name={`invoiceItems.${index}.quantity`}
                    value={cast(form.values.invoiceItems[index]?.quantity)}
                    onValueChange={(val) => {
                      const quantity = Number(val.value) ?? 1;
                      form.setFieldValue(`invoiceItems.${index}.quantity`, quantity);
                      item.quantity = quantity;
                      form.setFieldValue(`invoiceItems.${index}.amount`, calculateItemAmount(item));
                    }}
                    allowNegative={false}
                    thousandSeparator={true}
                    decimalScale={2}
                  />
                </Box>

                <Box className='flex-[0_0_13%]'>
                  <InputLabel>{t('estimateItems.description')}</InputLabel>
                  <TextField
                    name={`invoiceItems.${index}.description`}
                    value={cast(form.values.invoiceItems[index]?.description)}
                    onChange={form.handleChange}
                  />
                </Box>

                <Box className='flex-[0_0_10%]'>
                  <InputLabel>{t('estimateItems.tax')}</InputLabel>
                  <NumericFormat
                    customInput={TextField}
                    name={`invoiceItems.${index}.taxPercent`}
                    value={cast(form.values.invoiceItems[index]?.taxPercent)}
                    onValueChange={(val) => {
                      const tax = val.floatValue ?? 0;
                      form.setFieldValue(`invoiceItems.${index}.taxPercent`, tax);
                      item.taxPercent = tax;
                      form.setFieldValue(`invoiceItems.${index}.amount`, calculateItemAmount(item));
                    }}
                    decimalScale={2}
                  />
                </Box>

                <Box className='flex-[0_0_10%]'>
                  <InputLabel>{t('estimateItems.unitPrice')}</InputLabel>
                  <NumericFormat
                    customInput={TextField}
                    name={`invoiceItems.${index}.unitPrice`}
                    value={cast(form.values.invoiceItems[index]?.unitPrice)}
                    onValueChange={(val) => {
                      const unitPrice = val.floatValue ?? 0;
                      form.setFieldValue(`invoiceItems.${index}.unitPrice`, unitPrice);
                      item.unitPrice = unitPrice;
                      form.setFieldValue(`invoiceItems.${index}.amount`, calculateItemAmount(item));
                    }}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                  />
                </Box>
                <Box className='flex-[0_0_15%]'>
                  <InputLabel>{t('estimateItems.unit')}</InputLabel>
                  <BaseSelect
                    name={`invoiceItems.${index}.unit`}
                    value={cast(form.values.invoiceItems[index]?.unit)}
                    options={units}
                    onChange={form.handleChange}
                  />
                </Box>
                <Box className='flex-[0_0_15%]'>
                  <InputLabel>{t('estimateItems.area')}</InputLabel>
                  <BaseSelect
                    name={`invoiceItems.${index}.location`}
                    value={cast(form.values.invoiceItems[index]?.location)}
                    options={[{ id: 'job', name: 'Job' }, ...areaScopes]}
                    onChange={form.handleChange}
                  />
                </Box>
              </Box>
              <Box>
                <DeleteCircle className='w-[20px] h-[20px] mt-[20px]' onClick={() => handleDelete(index)} />
              </Box>
            </Box>
          ))}
        </Box>
        <AddEstimateItemDialog
          open={openAddEstimateItemDialog}
          setOpen={handleOpenAddEstimateItemDialog}
          selectedEstimateItems={handleSelectedEstimateItems}
          editData={form.values.invoiceItems}
          estimateItems={estimateItems}
        />
        <Typography className='text-red-600 text-[12px]'>
          {form.errors?.invoiceItems ? form.errors.invoiceItems : ''}
        </Typography>
        <Button className='mt-2' onClick={handleAddPriceItem}>
          {t('estimateItems.addNew')}
        </Button>
      </Box>
    </Box>
  );
}

export default memo(InvoiceListItem);
